<script setup lang="ts">
import { computed } from "vue";
import { trackSelection } from "@/analytics/drawing-analytics";
import { Facade } from "@/drawing";
import type { LayerInfo } from "@/drawing/managers";

import { Logger } from "@/logger";
import { useLayersList } from "@/composable/useLayersList";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";

defineProps<{
  targets: string[];
}>();

const { layers, updateLayers } = useLayersList();

const selectionLayerName = computed<string | null>(() => {
  if (!Facade.isInitialized) return null;

  // Get layers of selected entities
  const selectedLayers = Facade.selection.getSelectedLayers();

  // If all selected entities are on the same layer, return that layer name
  if (selectedLayers.length === 1) {
    return selectedLayers[0];
  }

  // Multiple layers selected or no selection
  return null;
});

function setLayer(layer: LayerInfo): void {
  trackSelection("layer_changed");
  Logger.info(`ChangeLayerBtn : set layer to ${layer.name}`);
  if (Facade.isInitialized) {
    Facade.selection.setLayer(layer.name);
  }
}
</script>
<template>
  <div>
    <button
      type="button"
      class="canvas-btn canvas-btn-md dropdown-toggle"
      data-bs-toggle="dropdown"
      style="aspect-ratio: unset; width: 50px; height: 36px"
      aria-expanded="false"
      @click="updateLayers"
    >
      <PhosphorIcon name="stack" :size="20" />
    </button>
    <ul
      class="dropdown-menu canvas-dropdown overflow-y-auto"
      style="max-height: 45vh"
    >
      <li>
        <h6 class="dropdown-header">
          {{ $t("layers") }}
        </h6>
      </li>
      <li
        v-for="layer in layers"
        :key="layer.name"
        class="dropdown-item"
        :class="{ active: layer.name == selectionLayerName }"
        @click="() => setLayer(layer)"
      >
        {{ layer.name }}
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
.canvas-dropdown {
  background-color: var(--canvas-bg);
  border: 1px solid var(--canvas-border);
  color: white;

  .dropdown-header {
    color: rgba(255, 255, 255, 0.4);
  }

  .dropdown-item {
    color: white;
    &:hover,
    &:focus {
      background-color: rgba(255, 255, 255, 0.08);
      color: white;
    }
    &.active {
      background-color: var(--canvas-btn-active-bg);
      color: white;
    }
  }
}
</style>
